import { apiFetchRussian } from './api';

export type PublicCorrectionStatus = 'PENDING' | 'APPROVED_AWAITING_RECALC' | 'REJECTED';

export type PublicCorrectionCard = {
  request_id: number;
  public_id: string;
  created_at: string;
  game_date: string;
  replay_name: string;
  replay_url: string;
  status: PublicCorrectionStatus;
  claimed_killer_name: string;
  claimed_killer_squad: string;
  claimed_killer_is_solo: boolean;
  claimed_victim_name: string;
  claimed_victim_squad: string;
  claimed_victim_is_solo: boolean;
  claimed_weapon_or_method: string;
  claimed_distance: number | null;
  public_rejection_reason?: string | null;
};

export type EvidenceInput = {
  url: string;
  label?: string;
};

export type PublicCorrectionInput = {
  smartcaptcha_token: string;
  website?: string;
  replay_url: string;
  event_time: string;
  claimed_killer_name: string;
  claimed_killer_squad: string;
  claimed_killer_is_solo: boolean;
  claimed_victim_name: string;
  claimed_victim_squad: string;
  claimed_victim_is_solo: boolean;
  claimed_weapon_or_method?: string;
  claimed_distance?: number | null;
  description?: string;
  evidence: EvidenceInput[];
  regulation_accepted: true;
};

export type PaginatedCorrections = {
  count: number;
  next: string | null;
  previous: string | null;
  results: PublicCorrectionCard[];
};

export type PublicCorrectionRegistryFilters = Partial<{
  requestId: string;
  createdDate: string;
  killer: string;
  victim: string;
  weapon: string;
  distance: string;
  replay: string;
  status: PublicCorrectionStatus;
  note: string;
}>;

export type PublicCorrectionRegistryOrdering =
  | 'requestId'
  | 'createdAt'
  | 'killer'
  | 'victim'
  | 'weapon'
  | 'distance'
  | 'replay'
  | 'status'
  | 'note';

export type ReplayOption = {
  id: number;
  replay_name: string;
  replay_date: string;
  replay_url: string;
};

export type ReplayParticipant = {
  nick: string;
  squad: string;
};

export const listPublicCorrections = ({
  page = 1,
  ordering,
  filters = {},
}: {
  page?: number;
  ordering?: PublicCorrectionRegistryOrdering | `-${PublicCorrectionRegistryOrdering}`;
  filters?: PublicCorrectionRegistryFilters;
} = {}): Promise<PaginatedCorrections> => {
  const params = new URLSearchParams({ page: String(page) });
  if (ordering) params.set('ordering', ordering);
  Object.entries(filters).forEach(([name, value]) => {
    if (value !== undefined && value !== '') params.set(name, value);
  });
  return apiFetchRussian<PaginatedCorrections>(`/manual-corrections/?${params}`);
};

export const submitPublicCorrection = (payload: PublicCorrectionInput): Promise<PublicCorrectionCard> =>
  apiFetchRussian<PublicCorrectionCard>('/manual-corrections/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const getPublicCorrection = (publicId: string): Promise<PublicCorrectionCard> =>
  apiFetchRussian<PublicCorrectionCard>(
    `/manual-corrections/${encodeURIComponent(publicId)}/status/`
  );

export const searchReplayOptions = (query: string): Promise<{ results: ReplayOption[] }> =>
  apiFetchRussian<{ results: ReplayOption[] }>(
    `/manual-corrections/options/replays/?q=${encodeURIComponent(query.trim())}`
  );

export const getReplayParticipants = (replayId: number): Promise<{ results: ReplayParticipant[] }> =>
  apiFetchRussian<{ results: ReplayParticipant[] }>(
    `/manual-corrections/options/replays/${replayId}/participants/`
  );
